import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, KeyRound, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 6) return setErr("Password must be at least 6 characters");
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND}/api/auth/reset-password`,
        { token, new_password: pw },
        { withCredentials: true },
      );
      setDone(true);
      setTimeout(() => navigate("/signin"), 1800);
    } catch (e) {
      // Unpack possible error shapes:
      //   string detail  -> "Invalid reset link"
      //   pydantic array -> [{msg, loc, type, ...}]
      //   no response    -> network / CORS
      const d = e?.response?.data?.detail;
      let msg = "Could not reset password";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = String(d[0].msg);
      else if (!e?.response) msg = "Network error — check your connection";
      // Log full error to console so we can debug if user reports issue
      // eslint-disable-next-line no-console
      console.error("Password reset failed:", e?.response?.status, e?.response?.data || e?.message);
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="kpi-label" style={{ color: "hsl(var(--oxblood))" }}>Invalid link</div>
          <h1 className="display text-2xl mt-2 font-bold uppercase tracking-tight">
            Missing reset token
          </h1>
          <p className="text-sm text-muted-foreground mt-3 mb-5">
            This link is incomplete. Request a fresh one.
          </p>
          <Link to="/forgot-password" className="hud-btn hud-btn-primary">
            Request reset link <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative">
      <div className="w-full max-w-md relative z-[2]">
        <div className="text-center mb-8">
          <div className="kpi-label">Set new password</div>
          <h1 className="display text-4xl mt-2 font-bold uppercase tracking-tight leading-none">
            Command<span className="text-oud">·</span>Center
          </h1>
          <div className="hairline-oud w-24 mx-auto mt-4" />
          <div className="serif-mark text-sm text-muted-foreground mt-3 tracking-wide">
            For CEO's Only
          </div>
        </div>

        {done ? (
          <div className="hud-frame p-8 text-center space-y-4" data-testid="reset-done">
            <div className="tread-plate h-1.5 -mx-8 -mt-8 mb-4" />
            <Check size={28} className="mx-auto text-hud" />
            <div className="display text-xl font-bold uppercase tracking-tight">
              Password updated
            </div>
            <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="hud-frame p-8 space-y-5" data-testid="reset-form">
            <div className="tread-plate h-1.5 -mx-8 -mt-8 mb-4" />
            <div>
              <div className="kpi-label">New password (6+ chars)</div>
              <input
                type="password"
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="hud-input mt-2"
                placeholder="••••••••"
                minLength={6}
                required
                data-testid="reset-pw"
              />
            </div>
            <div>
              <div className="kpi-label">Confirm new password</div>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="hud-input mt-2"
                placeholder="••••••••"
                minLength={6}
                required
                data-testid="reset-pw2"
              />
            </div>
            {err && (
              <div className="text-[hsl(var(--oxblood))] mono text-xs uppercase tracking-widest" data-testid="reset-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="hud-btn hud-btn-primary w-full justify-center"
              data-testid="reset-submit"
            >
              <KeyRound size={11} /> {busy ? "Resetting…" : "Reset password"}
            </button>
          </form>
        )}

        {!done && (
          <div className="mt-5 text-center mono text-[11px] uppercase tracking-widest text-muted-foreground">
            <Link to="/signin" className="text-foreground hover:text-hud">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
